<template>
  <base-table
    :loading="loading"
    :data="accounts"
    :empty-text="$t('settings.accountsTableEmpty')"
    stripe
    border
    class="accounts-table">

    <el-table-column
      prop="appLoginName"
      width="200"
      :label="$t('base.appLoginName')">
      <template slot-scope="account">
        <div class="cell-login">
          <span class="cell-login__avatar">{{ getInitials(account.row) }}</span>
          <span class="cell-login__name">{{ account.row.appLoginName }}</span>
        </div>
      </template>
    </el-table-column>

    <el-table-column prop="firstName" :label="$t('base.firstName')">
    </el-table-column>

    <el-table-column prop="lastName" :label="$t('base.lastName')">
    </el-table-column>

    <el-table-column prop="email" min-width="220" :label="$t('base.email')">
      <template slot-scope="account">
        <span class="cell-email">{{ account.row.email }}</span>
      </template>
    </el-table-column>

    <el-table-column
      align="center"
      width="160"
      :label="$t('settings.report_sort.sort_title')">
      <template slot-scope="account">
        <el-select
          v-model="account.row.reportSort"
          size="mini"
          class="compact-select"
          @change="updateAccount(account.row)">
          <el-option value="TYPE_DATE"    :label="$t('settings.report_sort.TYPE_DATE')"    key="TYPE_DATE"/>
          <el-option value="CREATED_DATE" :label="$t('settings.report_sort.CREATED_DATE')" key="CREATED_DATE"/>
          <el-option value="UPDATE_DATE"  :label="$t('settings.report_sort.UPDATE_DATE')"  key="UPDATE_DATE"/>
          <el-option value="START_DATE"   :label="$t('settings.report_sort.START_DATE')"   key="START_DATE"/>
        </el-select>
      </template>
    </el-table-column>

    <el-table-column align="center" width="100" :label="$t('settings.mail_reception')">
      <template slot-scope="account">
        <el-switch
          v-if="account.row.email"
          v-model="account.row.receivesMail"
          @change="updateAccount(account.row)">
        </el-switch>
        <span v-else class="no-email-hint">—</span>
      </template>
    </el-table-column>

    <el-table-column
      align="center"
      width="120"
      :label="$t('settings.authority')">
      <template slot-scope="account">
        <el-tag v-if="account.row.userAuthority === 'SUPER_ADMIN'" type="danger"  size="mini" class="role-tag role-tag--super">Super Admin</el-tag>
        <el-tag v-if="account.row.userAuthority === 'ADMIN'"       type="warning" size="mini" class="role-tag">Admin</el-tag>
        <el-tag v-if="account.row.userAuthority === 'REPORTER'"    type="info"    size="mini" class="role-tag">Reporter</el-tag>
        <el-tag v-if="account.row.userAuthority === 'USER'"        type="info"    size="mini" class="role-tag role-tag--user">User</el-tag>
      </template>
    </el-table-column>

    <el-table-column
      align="center"
      width="180"
      :label="$t('settings.notificationSettings.notificationTitle')">
      <template slot-scope="account">
        <el-select
          v-model="account.row.notificationSettings"
          size="mini"
          class="compact-select"
          @change="updateAccount(account.row)">
          <el-option value="NONE"          :label="$t('settings.notificationSettings.NONE')"          key="NONE"/>
          <el-option value="ONLY_DISORDER" :label="$t('settings.notificationSettings.ONLY_DISORDER')" key="ONLY_DISORDER"/>
          <el-option value="ALL"           :label="$t('settings.notificationSettings.ALL')"           key="ALL"/>
        </el-select>
      </template>
    </el-table-column>

    <el-table-column
      fixed="right"
      align="center"
      width="200"
      :label="$t('settings.operations')">
      <template slot-scope="account">
        <div class="action-buttons">
          <el-tooltip :content="$t('settings.account.sendEmail')" placement="top">
            <el-button
              :disabled="!account.row.email"
              size="small"
              type="primary"
              circle
              icon="el-icon-message"
              class="action-btn"
              @click="sendTestEmail(account.row)">
            </el-button>
          </el-tooltip>

          <el-tooltip :content="$t('settings.account.sendPush')" placement="top">
            <el-button
              :disabled="!account.row.hasPushToken"
              circle
              type="primary"
              size="small"
              icon="el-icon-mobile-phone"
              class="action-btn"
              @click="sendTestPush(account.row)">
            </el-button>
          </el-tooltip>

          <el-tooltip :content="$t('settings.account.resetPushToken')" placement="top">
            <el-button
              :disabled="!account.row.hasPushToken"
              circle
              type="warning"
              size="small"
              icon="el-icon-refresh"
              class="action-btn"
              @click="resetPushToken(account.row)">
            </el-button>
          </el-tooltip>

          <el-tooltip v-if="showLocalAccounts" :content="$t('settings.edit')" placement="top">
            <el-button
              type="primary"
              size="small"
              icon="el-icon-edit"
              circle
              class="action-btn"
              @click.stop.prevent="editAccount(account.row)">
            </el-button>
          </el-tooltip>

          <el-tooltip v-if="showLocalAccounts" :content="$t('settings.account.delete')" placement="top">
            <el-button
              circle
              type="danger"
              size="small"
              icon="el-icon-delete"
              class="action-btn"
              @click="deleteAccount(account.row)">
            </el-button>
          </el-tooltip>
        </div>
      </template>
    </el-table-column>
  </base-table>
</template>

<script>
import {adminAccountService} from "@/services/AdminAccountService.class";
import BaseTable from '@/components/common/BaseTable';

export default {
  name: "CustomerAccountsTable",
  components: {BaseTable},
  props: {
    accounts: {
      type: Array,
      default: () => []
    },
    showLocalAccounts: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    tenantId: {
      type: String,
      required: true
    },
    customerId: {
      type: String,
      required: true
    }
  },
  methods: {
    getInitials(row) {
      const name = `${row.firstName || ''} ${row.lastName || ''}`.trim() || row.appLoginName || '?';
      return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
    },
    updateAccount(account) {
      adminAccountService.updateAccount(account.id, account)
        .then(() => {
          this.$message.success(this.$t('settings.patchSuccess'));
          this.$emit('accountUpdated');
        })
        .catch(() => {
          this.$message.error(this.$t('settings.genericHttpError'));
        });
    },
    sendTestEmail(account) {
      adminAccountService.sendTestEmail(account.id)
        .then(() => { this.$message.success(this.$t('settings.testEmailSent')); })
        .catch(() => { this.$message.error(this.$t('settings.testEmailFailed')); });
    },
    sendTestPush(account) {
      adminAccountService.sendTestPush(account.id)
        .then(() => { this.$message.success(this.$t('settings.testPushSent')); })
        .catch(() => { this.$message.error(this.$t('settings.testPushFailed')); });
    },
    resetPushToken(account) {
      adminAccountService.deleteAllDeviceTokens(account.id)
        .then(() => {
          this.$message.success(this.$t('settings.pushTokensReset'));
          this.$emit('accountUpdated');
        })
        .catch(() => { this.$message.error(this.$t('settings.pushTokensResetFailed')); });
    },
    editAccount(account) {
      // Navigate to edit account page
      this.$router.push({
        path: `/app/tenants/${this.tenantId}/customers/${this.customerId}/accounts/${account.id}`
      });
    },
    deleteAccount(account) {
      this.$confirm(this.$t('settings.deleteAccountInfoText'), this.$t('settings.deleteAccount'), {
        confirmButtonClass: 'el-button--danger',
        confirmButtonText: this.$t('settings.deleteAccount'),
        cancelButtonText: this.$t('base.cancel'),
        type: 'warning'
      }).then(() => {
        adminAccountService.deleteAccount(account.id)
          .then(() => {
            this.$message.success(this.$t('settings.accountDeleted'));
            this.$emit('accountDeleted');
          })
          .catch(() => { this.$message.error(this.$t('settings.genericHttpError')); });
      });
    }
  }
}
</script>

<style scoped lang="scss">
.cell-login {
  display: flex;
  align-items: center;
  gap: 10px;

  &__avatar {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: linear-gradient(135deg, #409EFF, #2d7dd2);
    color: #fff;
    font-size: 11px;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  &__name {
    font-weight: 500;
    font-size: 13px;
  }
}

.cell-email {
  color: #606266;
  font-size: 13px;
}

.compact-select {
  width: 100%;

  :deep(.el-input__inner) {
    border-radius: 6px;
    font-size: 12.5px;
    height: 30px;
  }
}

.role-tag {
  border-radius: 20px;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.02em;
  padding: 0 8px;
}

.no-email-hint {
  color: #ccc;
}

.action-buttons {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  flex-wrap: wrap;
}

.action-btn {
  transition: transform 0.15s, box-shadow 0.15s;

  &:not(:disabled):hover {
    transform: translateY(-1px);
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.15);
  }
}
</style>
